import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Link, useOutletContext } from 'react-router-dom';
import {
  getVisibleLineFilterCount,
  isLineChecked,
  isShowingAllLines,
  isVisibleLineFilterActive,
  resolveVisibleLineOrder,
  usePersistedVisibleLines,
  visibleLinesSelectionBase,
} from './usePersistedVisibleLines';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';
import { isSnapshotBuilding, snapshotRetryDelayMs } from '../../services/snapshotState';
import {
  DEFAULT_VISIBILITY_TIMEFRAME,
  VISIBILITY_TIMEFRAME_OPTIONS,
  visibilityDeltaDirection,
  visibilityDeltaTitle,
} from '../../services/visibilityRange';
import { isCampaignViewer } from './aivisRoles';
import { clearbitLogoFromWebsite } from '../../utils/clearbitLogo';
import NoClientState from './NoClientState';
import { chartSeries, tokens } from './marketingTokens';
import './ai-vis.css';

const SERVICE_LABELS = {
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  google_ai_overviews: 'Google AI Overviews',
};

const LINE_COLORS = chartSeries;

/** Consistent order for per-service KPI tiles. */
const SERVICE_CHART_ORDER = ['chatgpt', 'google_ai_overviews'];

function RankingLogo({ logoUrl, label }) {
  const [failed, setFailed] = useState(false);
  if (logoUrl && !failed) {
    return (
      <img src={logoUrl} alt="" width={20} height={20} className="aivis-dash-rank-logo"
        onError={() => setFailed(true)} />
    );
  }
  return (
    <span className="aivis-dash-rank-logo aivis-dash-rank-logo--initial">
      {(label || '?')[0].toUpperCase()}
    </span>
  );
}

function formatShortDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function serviceIconHref(key) {
  if (key === 'chatgpt') return '/chatgpt-icon.png';
  if (key === 'perplexity') return '/perplexity-icon.png';
  if (key === 'google_ai_overviews') return '/google-icon.png';
  return null;
}

function ServiceLogo({ service, className }) {
  const src = serviceIconHref(service);
  if (!src) return <span className={className} />;
  return <img src={src} alt="" className={className} />;
}

function ActivityIcon({ activity }) {
  const svc = activity.meta?.service;
  const msg = (activity.message || '').toLowerCase();
  if (svc) {
    return (
      <span className="aivis-dash-activity-icon aivis-dash-activity-icon--service">
        <ServiceLogo service={svc} className="aivis-dash-activity-service-logo" />
      </span>
    );
  }
  if (msg.includes('run started')) {
    return (
      <span className="aivis-dash-activity-icon aivis-dash-activity-icon--neutral" aria-hidden>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 5v14M5 12h14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  }
  if (msg.includes('run finished') || msg.includes('run completed')) {
    const err = msg.includes('error');
    return (
      <span
        className={`aivis-dash-activity-icon ${err ? 'aivis-dash-activity-icon--warn' : 'aivis-dash-activity-icon--ok'}`}
        aria-hidden
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          {err ? (
            <path
              d="M12 9v4M12 17h.01M10.3 4.3h3.4l7.3 12.7H3L10.3 4.3Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          ) : (
            <path
              d="M20 6L9 17l-5-5"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </span>
    );
  }
  if (msg.includes('execution failed') || msg.includes('failed')) {
    return (
      <span className="aivis-dash-activity-icon aivis-dash-activity-icon--danger" aria-hidden>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return (
    <span className="aivis-dash-activity-icon aivis-dash-activity-icon--neutral" aria-hidden>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 10v5M12 7v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </span>
  );
}

const ACTIVITY_LIMIT = 30;
const DEFAULT_TIMEFRAME = DEFAULT_VISIBILITY_TIMEFRAME;
const TIMEFRAME_OPTIONS = VISIBILITY_TIMEFRAME_OPTIONS;
const GRAN_LABELS = { D: 'Daily', W: 'Weekly', M: 'Monthly' };
const ACTIVE_TREND_SERVICES = ['chatgpt', 'google_ai_overviews'];

function weekStart(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d;
}

function localISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function periodKeyAndLabel(dateStr, granularity) {
  if (granularity === 'W') {
    const ws = weekStart(dateStr);
    return {
      key: localISODate(ws),
      label: ws.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    };
  }
  const d = new Date(`${dateStr}T12:00:00`);
  return {
    key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
    label: `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]} '${String(d.getFullYear()).slice(2)}`,
  };
}

function meanActiveServiceScores(byService) {
  const scores = ACTIVE_TREND_SERVICES
    .filter((svc) => typeof byService[svc] === 'number')
    .map((svc) => byService[svc]);
  if (!scores.length) return 0;
  return Math.round((scores.reduce((sum, value) => sum + value, 0) / scores.length) * 10) / 10;
}

function VisibilityDelta({ value, available = true, basis }) {
  if (!available || typeof value !== 'number') {
    return null;
  }
  const direction = visibilityDeltaDirection(value);
  const prefix = value > 0 ? '+' : '';
  return (
    <span
      className={`aivis-visibility-delta aivis-visibility-delta--${direction}`}
      title={visibilityDeltaTitle(basis)}
    >
      {prefix}{value.toFixed(1)} pts
    </span>
  );
}

function normalizeTrendSeries(series) {
  return (series || []).map((row) => ({
    date: row.date,
    label: formatShortDate(row.date),
    n: row.n || 0,
    client_service: row.client_service || {},
    comp_hits: row.comp_hits || {},
    ...(row.metrics || {}),
  }));
}

function aggregateTrendData(data, granularity, lineOrder, aggregation = 'average') {
  if (granularity === 'D' || !data.length) return data;
  const groups = new Map();
  const order = [];
  const clientName = lineOrder[0];
  data.forEach((row) => {
    const { key, label } = periodKeyAndLabel(row.date, granularity);
    if (!groups.has(key)) {
      groups.set(key, {
        label,
        _s: {},
        _n: {},
        n: 0,
        clientService: {},
        compHits: {},
      });
      order.push(key);
    }
    const group = groups.get(key);
    group.n += row.n || 0;
    Object.entries(row.client_service || {}).forEach(([svc, stats]) => {
      const current = group.clientService[svc] || { total: 0, hits: 0 };
      current.total += stats.total || 0;
      current.hits += stats.hits || 0;
      group.clientService[svc] = current;
    });
    Object.entries(row.comp_hits || {}).forEach(([name, hits]) => {
      group.compHits[name] = (group.compHits[name] || 0) + hits;
    });
    lineOrder.forEach((line) => {
      if (typeof row[line] === 'number') {
        group._s[line] = (group._s[line] || 0) + row[line];
        group._n[line] = (group._n[line] || 0) + 1;
      }
    });
  });
  return order.map((key) => {
    const group = groups.get(key);
    const result = { label: group.label };
    if (aggregation === 'sum') {
      lineOrder.forEach((line) => {
        if (group._n[line]) result[line] = group._s[line];
      });
      return result;
    }
    if (aggregation === 'visibility' && (group.n || Object.keys(group.clientService).length)) {
      const byService = {};
      Object.entries(group.clientService).forEach(([svc, stats]) => {
        if (stats.total) {
          byService[svc] = Math.round((1000 * stats.hits) / stats.total) / 10;
        }
      });
      if (clientName && Object.keys(byService).length) {
        result[clientName] = meanActiveServiceScores(byService);
      }
      lineOrder.slice(1).forEach((line) => {
        result[line] = group.n
          ? Math.round((1000 * (group.compHits[line] || 0)) / group.n) / 10
          : 0;
      });
      return result;
    }
    lineOrder.forEach((line) => {
      if (group._n[line]) {
        result[line] = Math.round((group._s[line] / group._n[line]) * 10) / 10;
      }
    });
    return result;
  });
}

function trendYMax(data, visibleLineOrder) {
  const values = data.flatMap((row) => (
    visibleLineOrder.map((line) => (typeof row[line] === 'number' ? row[line] : 0))
  ));
  const peak = values.length ? Math.max(...values) : 0;
  return Math.min(100, Math.max(5, Math.ceil((peak + 5) / 5) * 5));
}

function countTrendYMax(data, visibleLineOrder) {
  const values = data.flatMap((row) => (
    visibleLineOrder.map((line) => (typeof row[line] === 'number' ? row[line] : 0))
  ));
  const peak = values.length ? Math.max(...values) : 0;
  return Math.max(5, Math.ceil((peak * 1.1) / 5) * 5);
}

function trendDateRange(data, granularity) {
  if (!data.length) return null;
  const first = data[0];
  const last = data[data.length - 1];
  if (granularity === 'D' && first.date) {
    const d1 = new Date(`${first.date}T12:00:00`);
    const d2 = last.date ? new Date(`${last.date}T12:00:00`) : d1;
    const days = Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
    const fmtShort = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const fmtFull = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const label = data.length === 1 ? fmtFull(d1) : `${fmtShort(d1)} – ${fmtFull(d2)}`;
    return { label, days };
  }
  const count = data.length;
  return {
    label: count >= 2 ? `${first.label} – ${last.label}` : first.label,
    days: count,
  };
}

function DashboardTrendChart({
  data,
  lineOrder,
  visibleLineOrder,
  brandColorMap,
  hoveredLine,
  setHoveredLine,
  yMax,
  tooltip,
  loading,
  chartRef,
  emptyMessage,
  compact = false,
  requirePositiveData = false,
  valueSuffix = '%',
  allowDecimals = true,
}) {
  const hasMetricData = !requirePositiveData || data.some((row) => (
    lineOrder.some((line) => typeof row[line] === 'number' && row[line] > 0)
  ));
  return (
    <div
      className={`aivis-dash-chart-wrap ${compact ? 'aivis-dash-chart-wrap--compact' : 'aivis-dash-chart-wrap--tall'}${loading ? ' aivis-skeleton-block' : ''}`}
      ref={chartRef}
    >
      {data.length && hasMetricData && visibleLineOrder.length ? (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="4 4" stroke={tokens.graphite200} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={tokens.graphite500} tickLine={false} />
            <YAxis
              domain={[0, yMax]}
              tick={{ fontSize: 11 }}
              stroke={tokens.graphite500}
              width={40}
              tickFormatter={(v) => `${v}${valueSuffix}`}
              allowDecimals={allowDecimals}
            />
            <Tooltip content={(props) => tooltip(props, valueSuffix)} />
            <Legend
              wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
              onMouseEnter={(item) => setHoveredLine(item.value)}
              onMouseLeave={() => setHoveredLine(null)}
            />
            {visibleLineOrder.map((key) => {
              const isHovered = hoveredLine === key;
              const isDimmed = hoveredLine && hoveredLine !== key;
              const globalIdx = lineOrder.indexOf(key);
              const stroke = brandColorMap[key] || LINE_COLORS[globalIdx % LINE_COLORS.length];
              return (
                <Line
                  key={key}
                  type="linear"
                  dataKey={key}
                  name={key}
                  stroke={stroke}
                  strokeWidth={isHovered ? 3 : (key === lineOrder[0] ? 2.2 : 1.8)}
                  strokeOpacity={isDimmed ? 0.15 : 1}
                  dot={{ r: 4, fill: stroke, stroke: '#fff', strokeWidth: 1.5 }}
                  activeDot={{ r: 6, fill: stroke, stroke: '#fff', strokeWidth: 2 }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      ) : data.length && hasMetricData && !visibleLineOrder.length ? (
        <p className="aivis-dash-empty">No competitors selected. Use the filter to choose which lines to display.</p>
      ) : (
        typeof emptyMessage === 'string'
          ? <p className="aivis-dash-empty">{emptyMessage}</p>
          : emptyMessage
      )}
    </div>
  );
}

function TrendLineFilter({
  open,
  setOpen,
  filterRef,
  lineOrder,
  visibleLines,
  setVisibleLines,
  brandColorMap,
}) {
  if (!lineOrder.length) return null;
  return (
    <div className="aivis-dash-line-filter-wrap" ref={filterRef}>
      <button
        type="button"
        className={`aivis-dash-chart-menu-btn${isVisibleLineFilterActive(lineOrder, visibleLines) ? ' aivis-dash-chart-menu-btn--active' : ''}`}
        onClick={() => setOpen((value) => !value)}
        title="Filter competitors"
        aria-label="Filter which competitors are shown"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        {isVisibleLineFilterActive(lineOrder, visibleLines) && (
          <span className="aivis-dash-filter-badge">
            {getVisibleLineFilterCount(lineOrder, visibleLines)}
          </span>
        )}
      </button>
      {open && (
        <div className="aivis-dash-line-filter-panel">
          <div className="aivis-dash-line-filter-header">
            <span className="aivis-dash-line-filter-title">Show competitors</span>
            <button
              type="button"
              className="aivis-dash-line-filter-all"
              onClick={() => {
                if (isShowingAllLines(lineOrder, visibleLines)) {
                  setVisibleLines(new Set([lineOrder[0]]));
                } else {
                  setVisibleLines(null);
                }
              }}
            >
              {isShowingAllLines(lineOrder, visibleLines) ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <ul className="aivis-dash-line-filter-list">
            {lineOrder.map((key, index) => {
              const checked = isLineChecked(key, lineOrder, visibleLines);
              const color = brandColorMap[key] || LINE_COLORS[index % LINE_COLORS.length];
              return (
                <li key={key} className="aivis-dash-line-filter-item">
                  <label className="aivis-dash-line-filter-label">
                    <input
                      type="checkbox"
                      className="aivis-dash-line-filter-check"
                      checked={checked}
                      onChange={() => {
                        const base = visibleLinesSelectionBase(lineOrder, visibleLines);
                        if (base.has(key)) base.delete(key);
                        else base.add(key);
                        setVisibleLines(base.size === lineOrder.length ? null : base);
                      }}
                    />
                    <span className="aivis-dash-line-filter-dot" style={{ background: color }} />
                    <span className="aivis-dash-line-filter-name">{key}</span>
                    {index === 0 && <span className="aivis-dash-line-filter-you">YOU</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function kpiStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('fail') || s.includes('error')) return 'aivis-dash-kpi-tile--tone-fail';
  if (s.includes('running') || s.includes('queued')) return 'aivis-dash-kpi-tile--tone-running';
  if (s.includes('complete')) return 'aivis-dash-kpi-tile--tone-done';
  return 'aivis-dash-kpi-tile--tone-neutral';
}

export default function AiVisDashboardPage() {
  const { selectedCampaignId, campaigns, refreshCampaigns, user } = useOutletContext();
  const canStartRun = !isCampaignViewer(user);
  const [dash, setDash] = useState(null);
  const [snapshotStatus, setSnapshotStatus] = useState('idle');
  const [snapshotBuildingSince, setSnapshotBuildingSince] = useState(null);
  const [snapshotPollCount, setSnapshotPollCount] = useState(0);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState('');
  const [hoveredLine, setHoveredLine] = useState(null);
  const [chartGranularity, setChartGranularity] = useState('D');
  const [citationGranularity, setCitationGranularity] = useState('D');
  const [timeframe, setTimeframe] = useState(DEFAULT_TIMEFRAME);
  const [timeframeReady, setTimeframeReady] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [citationFilterOpen, setCitationFilterOpen] = useState(false);
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const [insightView, setInsightView] = useState('citations');
  const filterRef = useRef(null);
  const citationFilterRef = useRef(null);
  const timeframeRef = useRef(null);
  const chartContainerRef = useRef(null);
  const citationChartContainerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [crawl, setCrawl] = useState(null); // { active, total, crawled, skipped, pending, uncrawled_count, failed_count }
  const [retrying, setRetrying] = useState(false);
  const [startRunOpen, setStartRunOpen] = useState(false);
  const [dropdownMounted, setDropdownMounted] = useState(false);
  const [dropdownClosing, setDropdownClosing] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [refreshingCounts, setRefreshingCounts] = useState(false);
  const startRunRef = useRef(null);
  const dashboardCacheRef = useRef(new Map());

  const selected = campaigns.find((c) => String(c.id) === String(selectedCampaignId));
  const noCampaign = !selectedCampaignId;
  const timeframeLabel = TIMEFRAME_OPTIONS.find(([value]) => value === timeframe)?.[1] || 'selected timeframe';
  const awaitingReport = !dash && (snapshotStatus === 'loading' || snapshotStatus === 'building');
  const reportUpdating = Boolean(dash) && (snapshotStatus === 'updating' || Boolean(dash.refreshing));
  const preparationDelayed = awaitingReport && snapshotPollCount > 0 && snapshotBuildingSince != null
    && Date.now() - snapshotBuildingSince > 30000;
  const showSkeleton = awaitingReport;
  const noCampaignMessage = campaigns.length === 0
    ? 'No clients available yet. Add a client to see data here.'
    : 'Select a client to see data here.';

  useEffect(() => {
    setTimeframeReady(false);
    if (!selectedCampaignId) {
      setTimeframe(DEFAULT_TIMEFRAME);
      setTimeframeReady(true);
      return;
    }
    try {
      const savedTimeframe = localStorage.getItem(
        `aivis-dashboard-timeframe:${selectedCampaignId}`
      );
      setTimeframe(
        TIMEFRAME_OPTIONS.some(([value]) => value === savedTimeframe)
          ? savedTimeframe
          : DEFAULT_TIMEFRAME
      );
      const savedView = localStorage.getItem(`aivis-dashboard-insight-view:${selectedCampaignId}`);
      setInsightView(savedView === 'prompts' ? 'prompts' : 'citations');
    } catch {
      setTimeframe(DEFAULT_TIMEFRAME);
      setInsightView('citations');
    }
    setTimeframeReady(true);
  }, [selectedCampaignId]);

  function selectTimeframe(next) {
    setTimeframe(next);
    setTimeframeOpen(false);
    try {
      localStorage.setItem(`aivis-dashboard-timeframe:${selectedCampaignId}`, next);
    } catch {
      // Storage can be disabled; the selection still works for this session.
    }
  }

  function selectInsightView(next) {
    setInsightView(next);
    setCitationFilterOpen(false);
    try {
      localStorage.setItem(`aivis-dashboard-insight-view:${selectedCampaignId}`, next);
    } catch {
      // Storage can be disabled; the selection still works for this session.
    }
  }

  // Persisted competitor filter — survives reloads, keyed per campaign
  const lineOrderForHook = useMemo(() => dash?.series_line_order || [], [dash?.series_line_order]);
  const [visibleLines, setVisibleLines] = usePersistedVisibleLines(selectedCampaignId, null, lineOrderForHook);

  // Close filter panel when campaign changes
  useEffect(() => {
    setFilterOpen(false);
    setCitationFilterOpen(false);
    setTimeframeOpen(false);
  }, [selectedCampaignId]);

  // Close filter panel on outside click
  useEffect(() => {
    if (!filterOpen) return;
    function onDown(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [filterOpen]);

  useEffect(() => {
    if (!citationFilterOpen) return;
    function onDown(e) {
      if (
        citationFilterRef.current
        && !citationFilterRef.current.contains(e.target)
      ) {
        setCitationFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [citationFilterOpen]);

  useEffect(() => {
    if (!timeframeOpen) return;
    function onDown(event) {
      if (timeframeRef.current && !timeframeRef.current.contains(event.target)) {
        setTimeframeOpen(false);
      }
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setTimeframeOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [timeframeOpen]);

  useEffect(() => {
    if (!selectedCampaignId || !timeframeReady) {
      setSnapshotStatus('idle');
      setSnapshotBuildingSince(null);
      return undefined;
    }

    const cacheKey = `${selectedCampaignId}:${timeframe}`;
    const cachedDashboard = dashboardCacheRef.current.get(cacheKey);
    let cancelled = false;
    let timer;
    let controller;
    let snapshotWasBuilding = false;
    let retryAttempt = 0;

    if (cachedDashboard) {
      setDash(cachedDashboard);
      setSnapshotStatus('ready');
      setSnapshotBuildingSince(null);
      setSnapshotPollCount(0);
    } else {
      // Never leave another timeframe's report on screen while the selected
      // range is still being assembled.
      setDash(null);
      setSnapshotStatus('loading');
      setSnapshotBuildingSince(null);
      setSnapshotPollCount(0);
    }
    setError('');

    const poll = async () => {
      if (cancelled) return;
      if (document.hidden) {
        timer = window.setTimeout(poll, 15000);
        return;
      }

      controller = new AbortController();
      const requests = [
        api.get(`/aivis/campaigns/${selectedCampaignId}/dashboard/?range=${timeframe}`, { signal: controller.signal }),
      ];
      // Activity is independent of the aggregate. Fetch it initially and after
      // a ready response, but do not multiply requests while a snapshot builds.
      if (!snapshotWasBuilding) {
        requests.push(api.get(`/aivis/campaigns/${selectedCampaignId}/activity/?limit=${ACTIVITY_LIMIT}`, { signal: controller.signal }));
      }
      const [dashboardResult, activityResult] = await Promise.allSettled(requests);
      if (cancelled) return;

      if (activityResult?.status === 'fulfilled') {
        setActivity(activityResult.value.data.results || []);
      }

      if (dashboardResult.status === 'fulfilled') {
        const payload = dashboardResult.value.data;
        if (isSnapshotBuilding(payload)) {
          const heldReport = dashboardCacheRef.current.get(cacheKey);
          if (heldReport) {
            setDash(heldReport);
            setSnapshotStatus('updating');
          } else {
            setDash(null);
            setSnapshotStatus('building');
          }
          setSnapshotBuildingSince((startedAt) => startedAt || Date.now());
          setSnapshotPollCount((count) => count + 1);
          snapshotWasBuilding = true;
          const baseDelay = snapshotRetryDelayMs(payload);
          const retryDelay = Math.min(30000, baseDelay * (2 ** Math.min(retryAttempt, 3)));
          retryAttempt += 1;
          timer = window.setTimeout(poll, retryDelay);
          return;
        }

        dashboardCacheRef.current.set(cacheKey, payload);
        setDash(payload);
        setSnapshotStatus('ready');
        setSnapshotBuildingSince(null);
        setSnapshotPollCount(0);
        snapshotWasBuilding = false;
        retryAttempt = 0;
        timer = window.setTimeout(poll, 15000);
        return;
      }

      if (!controller.signal.aborted) {
        setSnapshotStatus(cachedDashboard ? 'ready' : 'error');
        if (!cachedDashboard) {
          setError(dashboardResult.reason.response?.data?.detail || 'Could not load dashboard.');
        }
      }
      timer = window.setTimeout(poll, 15000);
    };

    poll();
    return () => {
      cancelled = true;
      controller?.abort();
      window.clearTimeout(timer);
    };
  }, [selectedCampaignId, timeframe, timeframeReady]);

  // Crawl-progress polling — only runs while there are pending sources.
  // On mount: one fetch to populate state. If pending > 0, a 3s interval starts.
  // The interval self-cancels the moment a response shows pending === 0.
  // Bumping crawlPollTrigger (after Start Run / crawl-uncrawled) restarts the whole effect.
  const [crawlPollTrigger, setCrawlPollTrigger] = useState(0);

  useEffect(() => {
    if (!selectedCampaignId) return undefined;
    let stopped = false;
    let intervalId = null;

    // Returns true if there are still pending sources (i.e. keep polling)
    async function fetchCrawl() {
      try {
        const res = await api.get(`/aivis/campaigns/${selectedCampaignId}/crawl-progress/`);
        if (stopped) return false;
        setCrawl(res.data);
        return (res.data?.pending ?? 0) > 0;
      } catch {
        return false; // on error, stop polling rather than hammering the server
      }
    }

    // Initial fetch — only start the interval if crawl is actually in progress
    fetchCrawl().then((hasPending) => {
      if (stopped || !hasPending) return; // idle — no interval needed
      intervalId = setInterval(async () => {
        const stillPending = await fetchCrawl();
        if (!stillPending) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 3000);
    });

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [selectedCampaignId, crawlPollTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close Start Run dropdown when clicking outside
  function openStartRunDropdown() {
    setDropdownClosing(false);
    setDropdownMounted(true);
    setStartRunOpen(true);
  }

  function closeStartRunDropdown() {
    setDropdownClosing(true);
    setStartRunOpen(false);
    // dropdownMounted cleared by onAnimationEnd on the dropdown element
  }

  function handleDropdownAnimationEnd() {
    if (dropdownClosing) {
      setDropdownMounted(false);
      setDropdownClosing(false);
    }
  }

  useEffect(() => {
    if (!startRunOpen) return;
    function handler(e) {
      if (startRunRef.current && !startRunRef.current.contains(e.target)) {
        closeStartRunDropdown();
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [startRunOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  async function runNow() {
    if (!selectedCampaignId) return;
    setRunning(true);
    closeStartRunDropdown();
    setError('');
    try {
      await api.post(`/aivis/campaigns/${selectedCampaignId}/run-now/`);
      await refreshCampaigns();
      // Kick off crawl-progress polling now that a run was queued
      setCrawlPollTrigger((n) => n + 1);
    } catch (e) {
      const data = e.response?.data || {};
      setError(data.can_cancel
        ? '__stale_run__'
        : (data.detail || 'Run could not be queued.'));
    } finally {
      setRunning(false);
    }
  }

  async function cancelActiveRun() {
    if (!selectedCampaignId) return;
    setCancelling(true);
    try {
      await api.post(`/aivis/campaigns/${selectedCampaignId}/cancel-active-run/`);
      setError('');
      await refreshCampaigns();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not cancel run.');
    } finally {
      setCancelling(false);
    }
  }

  async function crawlUncrawled() {
    if (!selectedCampaignId) return;
    setCrawling(true);
    closeStartRunDropdown();
    setError('');
    try {
      await api.post(`/aivis/campaigns/${selectedCampaignId}/crawl-uncrawled-sources/`);
      setCrawlPollTrigger((n) => n + 1);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not queue source crawl.');
    } finally {
      setCrawling(false);
    }
  }

  async function retryFailed() {
    if (!selectedCampaignId) return;
    setRetrying(true);
    closeStartRunDropdown();
    setError('');
    try {
      await api.post(`/aivis/campaigns/${selectedCampaignId}/retry-failed-sources/`);
      setCrawlPollTrigger((n) => n + 1);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not retry failed sources.');
    } finally {
      setRetrying(false);
    }
  }

  const uncrawledCount = crawl?.uncrawled_count ?? 0;
  const hasUncrawled = uncrawledCount > 0;
  const failedCount = crawl?.failed_count ?? 0;
  const hasFailedSources = failedCount > 0;
  const runGateLimit = crawl?.run_gate_limit ?? 200;
  const runGateBlocked = Boolean(crawl?.run_gate_blocked) && uncrawledCount > runGateLimit;

  async function handleStartRunClick() {
    if (!selectedCampaignId) return;
    if (startRunOpen) {
      closeStartRunDropdown();
      return;
    }
    // Open the dropdown INSTANTLY using the already-polled crawl state, so the
    // button feels responsive. crawl_progress for a large backlog client runs
    // several COUNTs over tens of thousands of rows (~0.5–1 s); awaiting it
    // before opening is what made the popup feel slow.
    if (hasUncrawled || hasFailedSources) {
      openStartRunDropdown();
    } else {
      // Nothing pending in the current state — take the shortcut and run now.
      // A background refresh is still fired below to confirm counts.
      runNow();
    }
    // Refresh counts in the background (non-blocking) so the gate / disabled
    // state on "Run Report" is up-to-date. If the fresh fetch reveals the
    // client is actually gated, the Run Report option flips to disabled.
    if (selectedCampaignId) {
      setRefreshingCounts(true);
      api.get(`/aivis/campaigns/${selectedCampaignId}/crawl-progress/`)
        .then((res) => {
          setCrawl(res.data);
          // If the fresh fetch shows there actually IS a backlog, make sure the
          // dropdown is open so the user can reach "Run Client Mention Check".
          const freshUncrawled = res.data?.uncrawled_count ?? 0;
          const freshFailed = res.data?.failed_count ?? 0;
          if ((freshUncrawled > 0 || freshFailed > 0) && !startRunOpen && !running) {
            openStartRunDropdown();
          }
        })
        .catch(() => {})
        .finally(() => setRefreshingCounts(false));
    }
  }

  const lineOrder = useMemo(() => dash?.series_line_order || [], [dash?.series_line_order]);

  // Map label → brand_color from rankings (used to color chart lines with real brand colors)
  const brandColorMap = useMemo(() => {
    const map = {};
    (dash?.rankings || []).forEach((r) => {
      if (r.brand_color) map[r.label] = r.brand_color;
    });
    return map;
  }, [dash?.rankings]);
  const chartData = useMemo(() => normalizeTrendSeries(dash?.series), [dash?.series]);
  const citationChartData = useMemo(
    () => normalizeTrendSeries(dash?.citation_series),
    [dash?.citation_series]
  );
  const aggregatedChartData = useMemo(
    () => aggregateTrendData(chartData, chartGranularity, lineOrder, 'visibility'),
    [chartData, chartGranularity, lineOrder]
  );
  const aggregatedCitationData = useMemo(
    () => aggregateTrendData(citationChartData, citationGranularity, lineOrder, 'sum'),
    [citationChartData, citationGranularity, lineOrder]
  );

  const visibleLineOrder = useMemo(
    () => resolveVisibleLineOrder(lineOrder, visibleLines),
    [lineOrder, visibleLines]
  );

  const visYMax = useMemo(
    () => trendYMax(aggregatedChartData, visibleLineOrder),
    [aggregatedChartData, visibleLineOrder]
  );
  const citationYMax = useMemo(
    () => countTrendYMax(aggregatedCitationData, visibleLineOrder),
    [aggregatedCitationData, visibleLineOrder]
  );
  const dateRange = useMemo(
    () => trendDateRange(aggregatedChartData, chartGranularity),
    [aggregatedChartData, chartGranularity]
  );
  const citationDateRange = useMemo(
    () => trendDateRange(aggregatedCitationData, citationGranularity),
    [aggregatedCitationData, citationGranularity]
  );

  async function downloadChartPng(chartRef, filename) {
    const card = chartRef.current?.closest('.aivis-dash-card') || chartRef.current;
    if (!card) return;
    // Reveal the meta text (hidden in live UI) only during the capture
    card.classList.add('aivis-exporting');
    try {
      const canvas = await html2canvas(card, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (el) => (
          el.classList?.contains('aivis-dash-vis-controls')
          || el.classList?.contains('aivis-dash-view-nav')
        ),
      });
      canvas.toBlob((blob) => {
        const a = document.createElement('a');
        a.download = filename;
        a.href = URL.createObjectURL(blob);
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      }, 'image/png');
    } catch (e) {
      console.error('PNG export failed', e);
    } finally {
      card.classList.remove('aivis-exporting');
    }
  }

  const renderLineTooltip = useCallback(
    ({ active, payload, label }, valueSuffix = '%') => {
      if (!active || !payload?.length) return null;
      return (
        <div className="aivis-dash-tooltip">
          <div className="aivis-dash-tooltip-title">{label}</div>
          {payload
            .filter((p) => p.dataKey !== 'label' && p.dataKey !== 'date')
            .map((p) => (
              <div key={String(p.dataKey)} className="aivis-dash-tooltip-row">
                <span className="aivis-dash-tooltip-dot" style={{ background: p.color }} />
                <span>{p.name}</span>
                <strong>{typeof p.value === 'number' ? `${p.value}${valueSuffix}` : p.value}</strong>
              </div>
            ))}
        </div>
      );
    },
    []
  );

  function formatNextRun(isoUtc, timezone) {
    if (!isoUtc) return null;
    try {
      return new Date(isoUtc).toLocaleString(undefined, {
        timeZone: timezone || 'UTC',
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
      });
    } catch { return new Date(isoUtc).toLocaleString(); }
  }

  const nextRunFormatted = selected ? formatNextRun(selected.next_run_at, selected.client_timezone) : null;

  if (noCampaign) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-dash">
      <header className="aivis-dash-header">
        <div>
          <h1 className="aivis-page-title aivis-dash-title">AI Visibility</h1>
          <p className="aivis-page-sub aivis-dash-sub">
            {selected
              ? `Tracking “${selected.client_name}” across AI surfaces.`
              : campaigns.length === 0
                ? 'No clients yet — add a client to see visibility, sources, and live activity.'
                : 'Create a campaign to see visibility, sources, and live activity.'}
          </p>
          {dash?.generated_at && (
            <p className="aivis-page-sub">
              Updated {new Date(dash.generated_at).toLocaleString()}
            </p>
          )}
          {(awaitingReport || reportUpdating) && (
            <p className="aivis-report-status" role="status">
              <span className="aivis-report-status-dot" aria-hidden />
              {reportUpdating
                ? `Updating the ${timeframeLabel.toLowerCase()} report`
                : preparationDelayed
                  ? 'This report is taking longer than usual. It will refresh automatically.'
                  : `Preparing your ${timeframeLabel.toLowerCase()} report.`}
            </p>
          )}
        </div>
        <div className="aivis-dash-header-actions">
          {selectedCampaignId && (
            <div className="aivis-dash-timeframe" ref={timeframeRef}>
              <button
                type="button"
                className={`aivis-dash-timeframe-trigger${timeframeOpen ? ' aivis-dash-timeframe-trigger--open' : ''}`}
                onClick={() => setTimeframeOpen((open) => !open)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    setTimeframeOpen(true);
                  }
                }}
                aria-haspopup="listbox"
                aria-expanded={timeframeOpen}
              >
                <span className="aivis-dash-timeframe-label">Timeframe</span>
                <span className="aivis-dash-timeframe-value">
                  {TIMEFRAME_OPTIONS.find(([value]) => value === timeframe)?.[1] || 'Last 30 days'}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {timeframeOpen && (
                <div
                  className="aivis-dash-timeframe-menu"
                  role="listbox"
                  aria-label="Dashboard timeframe"
                >
                  {TIMEFRAME_OPTIONS.map(([value, label]) => {
                    const selectedOption = value === timeframe;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="option"
                        aria-selected={selectedOption}
                        className={`aivis-dash-timeframe-option${selectedOption ? ' aivis-dash-timeframe-option--selected' : ''}`}
                        onClick={() => selectTimeframe(value)}
                      >
                        <span>{label}</span>
                        {selectedOption && (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {selectedCampaignId && canStartRun ? (
            <div className="aivis-start-run-wrap" ref={startRunRef}>
              <button
                type="button"
                className={`aivis-btn-primary aivis-start-run-btn${(hasUncrawled || hasFailedSources) ? ' aivis-start-run-btn--has-dropdown' : ''}`}
                onClick={handleStartRunClick}
                disabled={running || crawling || retrying}
              >
                {running ? 'Queueing…' : crawling ? 'Crawling…' : retrying ? 'Retrying…' : 'Start Run'}
                {(hasUncrawled || hasFailedSources) && !running && !crawling && !retrying && (
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points={startRunOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                  </svg>
                )}
              </button>

              {dropdownMounted && (hasUncrawled || hasFailedSources) && (
                <div
                  className={`aivis-start-run-dropdown${dropdownClosing ? ' aivis-start-run-dropdown--closing' : ''}`}
                  role="menu"
                  onAnimationEnd={handleDropdownAnimationEnd}
                >
                  <button
                    type="button"
                    className={`aivis-start-run-option${(runGateBlocked || refreshingCounts) ? ' aivis-start-run-option--disabled' : ''}`}
                    role="menuitem"
                    disabled={runGateBlocked || refreshingCounts}
                    title={runGateBlocked
                      ? `Blocked: ${uncrawledCount.toLocaleString()} uncrawled sources exceed the ${runGateLimit} limit. Run the Client Mention Check first.`
                      : (refreshingCounts ? 'Refreshing counts…' : undefined)}
                    onClick={(runGateBlocked || refreshingCounts) ? undefined : runNow}
                  >
                    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                    <span>
                      <strong>Run Report</strong>
                      <span className="aivis-start-run-option-sub">
                        {refreshingCounts
                          ? 'Checking uncrawled sources…'
                          : runGateBlocked
                            ? `Blocked — ${uncrawledCount.toLocaleString()} uncrawled > ${runGateLimit} limit`
                            : `for ${selected?.client_name || 'this campaign'}`}
                      </span>
                    </span>
                  </button>

                  {hasUncrawled && (
                    <>
                      <div className="aivis-start-run-divider" />
                      <button
                        type="button"
                        className="aivis-start-run-option"
                        role="menuitem"
                        onClick={crawlUncrawled}
                      >
                        <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <span>
                          <strong>Run Client Mention Check</strong>
                          <span className="aivis-start-run-option-sub">
                            {uncrawledCount.toLocaleString()} uncrawled source{uncrawledCount === 1 ? '' : 's'}
                          </span>
                        </span>
                      </button>
                    </>
                  )}

                  {hasFailedSources && (
                    <>
                      <div className="aivis-start-run-divider" />
                      <button
                        type="button"
                        className="aivis-start-run-option aivis-start-run-option--warn"
                        role="menuitem"
                        onClick={retryFailed}
                      >
                        <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <polyline points="1 4 1 10 7 10" />
                          <path d="M3.51 15a9 9 0 1 0 .49-4.5" />
                        </svg>
                        <span>
                          <strong>Retry Failed Sources</strong>
                          <span className="aivis-start-run-option-sub">
                            {failedCount.toLocaleString()} failed — retry once, then delete if still failing
                          </span>
                        </span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </header>

      {error && (
        error === '__stale_run__' ? (
          <div className="aivis-auth-error aivis-auth-error--stale">
            <span>A run appears to be stuck. You can cancel it and start a fresh one.</span>
            <button
              type="button"
              className="aivis-stale-run-cancel-btn"
              onClick={cancelActiveRun}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling…' : 'Cancel stuck run'}
            </button>
          </div>
        ) : (
          <div className="aivis-auth-error">{error}</div>
        )
      )}

      {/* ── Next run schedule banner ── */}
      {selected && nextRunFormatted && (
        <div className="aivis-next-run-banner">
          <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>
            <strong>Next run:</strong>{' '}{nextRunFormatted}
          </span>
        </div>
      )}

      {selected && selected.enabled === false ? (
        <p className="aivis-page-sub aivis-dashboard-paused-notice">
          Scheduled runs are paused for this campaign.
          {canStartRun ? ' Start Run still queues a manual run.' : ''}
        </p>
      ) : null}

      <div className={`aivis-dash-kpis${showSkeleton ? ' aivis-dash-kpis--loading' : ''}`} role="list" aria-label="Campaign summary">
        {/* Overall visibility */}
        <div className="aivis-dash-kpi-tile" role="listitem">
          <div className="aivis-dash-kpi-icon aivis-dash-kpi-icon--visibility" aria-hidden>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g className="aivis-magnify-pulse">
                <circle cx="11" cy="11" r="4" stroke="currentColor" strokeWidth="2"/>
                <line x1="14.5" y1="14.5" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </g>
            </svg>
          </div>
          <div className="aivis-dash-kpi-body">
            <span className="aivis-dash-kpi-eyebrow">Performance</span>
            <span className="aivis-dash-kpi-value">
              {dash?.summary_visibility_pct != null ? `${dash.summary_visibility_pct}%` : '—'}
            </span>
            <span className="aivis-dash-kpi-label">Visibility Score</span>
            <VisibilityDelta
              value={dash?.comparison?.summary_delta_points}
              available={dash?.comparison?.available}
              basis={dash?.comparison?.basis}
            />
            <span className="aivis-dash-kpi-hint">Average of ChatGPT and Google visibility — brand named in the AI output</span>
          </div>
        </div>
        {/* Per-service visibility tiles */}
        {SERVICE_CHART_ORDER.map((svcKey) => {
          const svcScore = dash?.by_service?.[svcKey];
          const svcLabel = SERVICE_LABELS[svcKey] || svcKey;
          const svcIcon = serviceIconHref(svcKey);
          return (
            <div key={svcKey} className="aivis-dash-kpi-tile" role="listitem">
              <div className="aivis-dash-kpi-icon aivis-dash-kpi-icon--service" aria-hidden>
                {svcIcon
                  ? <img src={svcIcon} alt={svcLabel} width={24} height={24} />
                  : <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>{svcLabel[0]}</span>
                }
              </div>
              <div className="aivis-dash-kpi-body">
                <span className="aivis-dash-kpi-eyebrow">{svcLabel}</span>
                <span className="aivis-dash-kpi-value">
                  {svcScore != null ? `${svcScore}%` : '—'}
                </span>
                <span className="aivis-dash-kpi-label">Visibility Score</span>
                <VisibilityDelta
                  value={dash?.comparison?.by_service_delta_points?.[svcKey]}
                  available={dash?.comparison?.available}
                  basis={dash?.comparison?.basis}
                />
                <span className="aivis-dash-kpi-hint">% of {svcLabel} runs where your brand was named in the AI output</span>
              </div>
            </div>
          );
        })}
      </div>


      {/* Section 1 */}
      <section className="aivis-dash-section" aria-label="Visibility and rankings">
        <div className="aivis-dash-row aivis-dash-row--7-3">
          <div className="aivis-dash-card">
            <div className="aivis-dash-card-head aivis-dash-vis-head">
              {/* Left: title + date range (only when there's a real range, i.e. 2+ data points) */}
              <h2 className="aivis-dash-card-title">
                Visibility
                {dateRange && aggregatedChartData.length >= 2 && (
                  <span className="aivis-dash-vis-daterange"> · {dateRange.label}</span>
                )}
              </h2>

              {/* Right: meta text (captured in PNG) + controls (excluded from PNG) */}
              <div className="aivis-dash-vis-right">
                {dateRange && (
                  <span className="aivis-dash-vis-meta">
                    {chartGranularity === 'D'
                      ? `${dateRange.days} ${dateRange.days === 1 ? 'day' : 'days'}`
                      : chartGranularity === 'W'
                        ? `${aggregatedChartData.length} ${aggregatedChartData.length === 1 ? 'week' : 'weeks'}`
                        : `${aggregatedChartData.length} ${aggregatedChartData.length === 1 ? 'month' : 'months'}`
                    } · {GRAN_LABELS[chartGranularity]}
                  </span>
                )}
                {/* Controls excluded from PNG export */}
                <div className="aivis-dash-vis-controls">
                  <div className="aivis-dash-gran-toggle">
                    <div
                      className="aivis-dash-gran-slider"
                      style={{ transform: `translateX(${['D','W','M'].indexOf(chartGranularity) * 100}%)` }}
                      aria-hidden
                    />
                    {[['D','Daily'],['W','Weekly'],['M','Monthly']].map(([g, title]) => (
                      <button
                        key={g}
                        type="button"
                        title={title}
                        className={`aivis-dash-gran-btn${chartGranularity === g ? ' aivis-dash-gran-btn--active' : ''}`}
                        onClick={() => setChartGranularity(g)}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                  <TrendLineFilter
                    open={filterOpen}
                    setOpen={setFilterOpen}
                    filterRef={filterRef}
                    lineOrder={lineOrder}
                    visibleLines={visibleLines}
                    setVisibleLines={setVisibleLines}
                    brandColorMap={brandColorMap}
                  />
                  <button
                    type="button"
                    className="aivis-dash-chart-menu-btn"
                    onClick={() => downloadChartPng(chartContainerRef, 'visibility-chart.png')}
                    title="Download as PNG"
                    aria-label="Download chart as PNG"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <DashboardTrendChart
              data={aggregatedChartData}
              lineOrder={lineOrder}
              visibleLineOrder={visibleLineOrder}
              brandColorMap={brandColorMap}
              hoveredLine={hoveredLine}
              setHoveredLine={setHoveredLine}
              yMax={visYMax}
              tooltip={renderLineTooltip}
              loading={awaitingReport}
              chartRef={chartContainerRef}
              emptyMessage={noCampaign ? noCampaignMessage : 'No completed runs in this period.'}
            />
          </div>

          <div className="aivis-dash-card">
            <div className="aivis-dash-card-head aivis-dash-card-head--row">
              <div>
                <h2 className="aivis-dash-card-title">Rankings</h2>
                <p className="aivis-dash-card-desc">Your brand vs. tracked competitors by visibility in AI outputs.</p>
              </div>
              <a href="/ai-vis/competitors" className="aivis-dash-rank-expand-btn" title="View all competitors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </a>
            </div>
            <div className="aivis-dash-rankings">
              {showSkeleton ? (
                <ul className="aivis-dash-rank-list">
                  {[72, 58, 50, 44, 38, 30].map((w, i) => (
                    <li key={i} className="aivis-dash-rank-row aivis-dash-rank-row--skeleton">
                      <span className="aivis-skel aivis-skel--circle" />
                      <span className="aivis-skel aivis-skel--circle" />
                      <span className="aivis-skel" style={{ width: `${w}%` }} />
                      <span className="aivis-skel aivis-skel--num" />
                    </li>
                  ))}
                </ul>
              ) : noCampaign ? (
                <p className="aivis-dash-empty">{noCampaignMessage}</p>
              ) : (dash?.rankings || []).length ? (
                <ul className="aivis-dash-rank-list">
                  {dash.rankings.slice(0, 6).map((row, idx) => (
                    <li
                      key={`${row.label}-${idx}`}
                      className={`aivis-dash-rank-row${hoveredLine === row.label ? ' aivis-dash-rank-row--hovered' : ''}`}
                      onMouseEnter={() => setHoveredLine(row.label)}
                      onMouseLeave={() => setHoveredLine(null)}
                    >
                      <span className="aivis-dash-rank-num">{idx + 1}</span>
                      <span className="aivis-dash-rank-logo-dot">
                        <RankingLogo logoUrl={row.logo_url} label={row.label} />
                        <span className="aivis-dash-rank-line-dot" style={{ background: brandColorMap[row.label] || LINE_COLORS[idx % LINE_COLORS.length] }} aria-hidden />
                      </span>
                      <span className={`aivis-dash-rank-name ${row.is_client ? 'aivis-dash-rank-name--client' : ''}`}>
                        {row.label}
                        {row.is_client && <span className="aivis-comp-you-tag" style={{ marginLeft: 6 }}>You</span>}
                      </span>
                      <span className="aivis-dash-rank-metric">
                        <span className="aivis-dash-rank-pct">{row.visibility_pct != null ? `${row.visibility_pct}%` : '—'}</span>
                        <VisibilityDelta
                          value={row.delta_points}
                          available={dash?.comparison?.available}
                          basis={dash?.comparison?.basis}
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="aivis-dash-empty">Add competitors on the Competitors page to see rankings.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 — Citations/Top Prompts LEFT, Top Domains RIGHT */}
      <section className="aivis-dash-section" aria-label="Citations, top prompts, and domains">
        <div className="aivis-dash-row aivis-dash-row--equal">

          {/* Switchable Citations / Top Prompts — left */}
          <div className="aivis-dash-card aivis-dash-card--col">
            <div className="aivis-dash-card-head aivis-dash-card-head--row">
              <div>
                <h2 className="aivis-dash-card-title">
                  {insightView === 'citations' ? 'Citations' : 'Top prompts'}
                  {insightView === 'citations' && citationDateRange && aggregatedCitationData.length >= 2 && (
                    <span className="aivis-dash-vis-daterange"> · {citationDateRange.label}</span>
                  )}
                </h2>
                <p className="aivis-dash-card-desc">
                  {insightView === 'citations'
                    ? 'How many times each brand’s domains were cited as a source. This is a count, not a visibility rate.'
                    : 'Prompts ranked by visibility score — highest mention rate first.'}
                </p>
              </div>
              <div className="aivis-dash-view-nav" aria-label="Choose insights view">
                <button
                  type="button"
                  className="aivis-dash-view-arrow"
                  onClick={() => selectInsightView(insightView === 'citations' ? 'prompts' : 'citations')}
                  aria-label="Previous insights view"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <div className="aivis-dash-view-toggle">
                  {[
                    ['citations', 'Citations'],
                    ['prompts', 'Prompts'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={insightView === value ? 'aivis-dash-view-toggle-btn aivis-dash-view-toggle-btn--active' : 'aivis-dash-view-toggle-btn'}
                      onClick={() => selectInsightView(value)}
                      aria-pressed={insightView === value}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="aivis-dash-view-arrow"
                  onClick={() => selectInsightView(insightView === 'citations' ? 'prompts' : 'citations')}
                  aria-label="Next insights view"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
            {insightView === 'citations' ? (
              <div className="aivis-dash-card-body">
                <div className="aivis-dash-secondary-chart-controls aivis-dash-vis-controls">
                  <div className="aivis-dash-gran-toggle">
                    <div
                      className="aivis-dash-gran-slider"
                      style={{ transform: `translateX(${['D','W','M'].indexOf(citationGranularity) * 100}%)` }}
                      aria-hidden
                    />
                    {[['D','Daily'],['W','Weekly'],['M','Monthly']].map(([granularity, title]) => (
                      <button
                        key={granularity}
                        type="button"
                        title={title}
                        className={`aivis-dash-gran-btn${citationGranularity === granularity ? ' aivis-dash-gran-btn--active' : ''}`}
                        onClick={() => setCitationGranularity(granularity)}
                      >
                        {granularity}
                      </button>
                    ))}
                  </div>
                  <TrendLineFilter
                    open={citationFilterOpen}
                    setOpen={setCitationFilterOpen}
                    filterRef={citationFilterRef}
                    lineOrder={lineOrder}
                    visibleLines={visibleLines}
                    setVisibleLines={setVisibleLines}
                    brandColorMap={brandColorMap}
                  />
                  <button
                    type="button"
                    className="aivis-dash-chart-menu-btn"
                    onClick={() => downloadChartPng(citationChartContainerRef, 'citations-chart.png')}
                    title="Download as PNG"
                    aria-label="Download citations chart as PNG"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </button>
                </div>
                <DashboardTrendChart
                  data={aggregatedCitationData}
                  lineOrder={lineOrder}
                  visibleLineOrder={visibleLineOrder}
                  brandColorMap={brandColorMap}
                  hoveredLine={hoveredLine}
                  setHoveredLine={setHoveredLine}
                  yMax={citationYMax}
                  tooltip={renderLineTooltip}
                  loading={awaitingReport}
                  chartRef={citationChartContainerRef}
                  emptyMessage={noCampaign ? noCampaignMessage : 'No citations in this period.'}
                  compact
                  requirePositiveData
                  valueSuffix=""
                  allowDecimals={false}
                />
              </div>
            ) : (
              <div className="aivis-dash-card-body aivis-dash-table-scroll">
                {showSkeleton ? (
                  <table className="aivis-dash-table">
                    <tbody>
                      {[85, 70, 75, 60, 80, 65, 72, 55, 68, 50].map((w, i) => (
                        <tr key={i} className="aivis-dash-skel-row">
                          <td><span className="aivis-skel" style={{ width: `${w}%`, height: 13, borderRadius: 5, display: 'inline-block' }} /></td>
                          <td><span className="aivis-skel aivis-skel--num" /></td>
                          <td><span className="aivis-skel aivis-skel--num" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : noCampaign ? (
                  <p className="aivis-dash-empty aivis-dash-empty--inset">{noCampaignMessage}</p>
                ) : dash?.top_prompts?.length ? (
                  <table className="aivis-dash-table aivis-dash-table--prompts">
                    <thead>
                      <tr>
                        <th>Prompt</th>
                        <th>Visibility</th>
                        <th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dash.top_prompts.map((row, idx) => (
                        <tr key={row.prompt_id} className="aivis-dash-prompt-row">
                          <td>
                            <Link
                              to={`/ai-vis/prompts/${row.prompt_id}`}
                              className="aivis-dash-prompt-link"
                              title={row.prompt_text}
                            >
                              <span className="aivis-dash-prompt-rank">{idx + 1}</span>
                              <span className="aivis-dash-prompt-text">
                                {row.prompt_text.length > 70
                                  ? `${row.prompt_text.slice(0, 70)}…`
                                  : row.prompt_text || `Prompt ${idx + 1}`}
                              </span>
                            </Link>
                          </td>
                          <td className="aivis-dash-prompt-num">
                            <span className={`aivis-dash-prompt-vis${row.mention_pct >= 50 ? ' aivis-dash-prompt-vis--high' : row.mention_pct >= 25 ? ' aivis-dash-prompt-vis--mid' : row.mention_pct < 20 ? ' aivis-dash-prompt-vis--low' : ''}`}>
                              {row.mention_pct}%
                            </span>
                          </td>
                          <td className="aivis-dash-prompt-num">
                            <VisibilityDelta
                              value={row.delta_points}
                              available={dash?.comparison?.available}
                              basis={dash?.comparison?.basis}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="aivis-dash-empty aivis-dash-empty--inset">No prompt executions in this period.</p>
                )}
              </div>
            )}
          </div>

          {/* Top domains — right */}
          <div className="aivis-dash-card aivis-dash-card--col">
            <div className="aivis-dash-card-head">
              <h2 className="aivis-dash-card-title">Top domains</h2>
              <p className="aivis-dash-card-desc">Top 10 domains by retrieval rate in the selected timeframe.</p>
            </div>
            <div className="aivis-dash-card-body aivis-dash-table-scroll">
              {showSkeleton ? (
                <table className="aivis-dash-table">
                  <tbody>
                    {[80, 65, 70, 55, 75, 60, 68, 50, 72, 58].map((w, i) => (
                      <tr key={i} className="aivis-dash-skel-row">
                        <td><span className="aivis-dash-domain-cell"><span className="aivis-skel aivis-skel--favicon" /><span className="aivis-skel" style={{ width: `${w}%` }} /></span></td>
                        <td><span className="aivis-skel aivis-skel--num" /></td>
                        <td><span className="aivis-skel aivis-skel--num" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : noCampaign ? (
                <p className="aivis-dash-empty aivis-dash-empty--inset">{noCampaignMessage}</p>
              ) : dash?.top_domains?.length ? (
                <table className="aivis-dash-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th title="This domain's citations as a % of all citations — matches the Domains report">Citation Rate</th>
                      <th title="Total times a URL from this domain appeared as a source">Citations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dash.top_domains.map((row) => (
                      <tr key={row.domain}>
                        <td>
                          <Link
                            to="/ai-vis/sources"
                            state={{ initialSearch: row.domain }}
                            className="aivis-dash-domain-cell aivis-dash-domain-cell--link"
                            title={`View sources for ${row.domain}`}
                          >
                            <img
                              src={clearbitLogoFromWebsite(row.domain)}
                              alt=""
                              className="aivis-dash-domain-favicon"
                              onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                            />
                            {row.domain}
                          </Link>
                        </td>
                        <td>
                          {row.citation_share != null ? `${row.citation_share}%` : '—'}
                          {typeof row.delta_points === 'number' && (
                            <>
                              <br />
                              <VisibilityDelta
                                value={row.delta_points}
                                available={dash?.comparison?.available}
                                basis={dash?.comparison?.basis}
                              />
                            </>
                          )}
                        </td>
                        <td>{row.citations != null ? row.citations.toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="aivis-dash-empty aivis-dash-empty--inset">No source URLs recorded yet.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="aivis-dash-section" aria-label="Live activity">
        <div className="aivis-dash-card">
          <div className="aivis-dash-card-head">
            <h2 className="aivis-dash-card-title">Live activity</h2>
            <p className="aivis-dash-card-desc">Latest {ACTIVITY_LIMIT} worker and model events (updates automatically).</p>
          </div>
          <div className="aivis-dash-activity-scroll">
            <ul className="aivis-dash-activity-list">
              {awaitingReport && activity.length === 0 ? (
                [1,2,3,4,5].map((i) => (
                  <li key={i} className="aivis-dash-activity-item aivis-dash-activity-item--skeleton">
                    <span className="aivis-skeleton-icon" />
                    <div className="aivis-dash-activity-body">
                      <div className="aivis-skeleton-line" style={{ width: `${55 + i * 7}%` }} />
                      <div className="aivis-skeleton-line aivis-skeleton-line--sm" style={{ width: '30%' }} />
                    </div>
                  </li>
                ))
              ) : activity.length === 0 ? (
                <li className="aivis-dash-empty">
                  {noCampaign ? noCampaignMessage : 'No activity yet for this campaign.'}
                </li>
              ) : (
                activity.map((a) => (
                  <li key={a.id} className="aivis-dash-activity-item">
                    <ActivityIcon activity={a} />
                    <div className="aivis-dash-activity-body">
                      <div className="aivis-dash-activity-msg">{a.message}</div>
                      <time className="aivis-dash-activity-time" dateTime={a.created_at}>
                        {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                      </time>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </section>

      {/* Volume + Status — bottom row */}
      <div className={`aivis-dash-kpis aivis-dash-kpis--two${showSkeleton ? ' aivis-dash-kpis--loading' : ''}`} role="list" aria-label="Campaign volume and status">
        <div className="aivis-dash-kpi-tile" role="listitem">
          <div className="aivis-dash-kpi-icon aivis-dash-kpi-icon--runs" aria-hidden>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g className="aivis-ring-outer">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25"/>
                <path d="M12 3 A9 9 0 1 1 5.4 18.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </g>
              <g className="aivis-ring-middle">
                <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25"/>
                <path d="M12 6 A6 6 0 1 1 7.2 15.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </g>
              <g className="aivis-ring-inner">
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25"/>
                <path d="M12 9 A3 3 0 0 1 15 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </g>
            </svg>
          </div>
          <div className="aivis-dash-kpi-body">
            <span className="aivis-dash-kpi-eyebrow">Volume</span>
            <span className="aivis-dash-kpi-value">{dash?.runs_total ?? '—'}</span>
            <span className="aivis-dash-kpi-label">Total runs</span>
            <span className="aivis-dash-kpi-hint">All tracking cycles for this campaign</span>
          </div>
        </div>
        <div className={`aivis-dash-kpi-tile ${kpiStatusClass(dash?.last_run_status)}`} role="listitem">
          <div className="aivis-dash-kpi-icon aivis-dash-kpi-icon--status" aria-hidden>
            {(() => {
              const s = String(dash?.last_run_status || '').toLowerCase();
              const isRunning = s.includes('running') || s.includes('queued');
              const isDone = s.includes('complete');
              if (isRunning) return (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <g className="aivis-gear-big">
                    <circle cx="15.5" cy="8.5" r="3" stroke="currentColor" strokeWidth="2"/>
                    <path d="M15.5 3.5V5.5M15.5 11.5V13.5M20.5 8.5H18.5M12.5 8.5H10.5M18.9 5.1L17.5 6.5M13.5 10.5L12.1 11.9M18.9 11.9L17.5 10.5M13.5 6.5L12.1 5.1"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </g>
                  <g className="aivis-gear-small">
                    <circle cx="7.5" cy="10.5" r="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7.5 7.5V8.5M7.5 12.5V13.5M10.5 10.5H9.5M5.5 10.5H4.5M9.2 8.8L8.5 9.5M6.5 11.5L5.8 12.2M9.2 12.2L8.5 11.5M6.5 9.5L5.8 8.8"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </g>
                  <rect x="3" y="17" width="18" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.3"/>
                  <rect className="aivis-progress-fill" x="3" y="17" height="3" rx="1.5" fill="currentColor" opacity="0.75"/>
                </svg>
              );
              if (isDone) return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <g className="aivis-radial-spin">
                    <g className="aivis-radial-blink-a" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="12" y1="2" x2="12" y2="4"/>
                      <line x1="12" y1="20" x2="12" y2="22"/>
                      <line x1="2" y1="12" x2="4" y2="12"/>
                      <line x1="20" y1="12" x2="22" y2="12"/>
                    </g>
                    <g className="aivis-radial-blink-b" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="4.5" y1="4.5" x2="6" y2="6"/>
                      <line x1="18" y1="18" x2="19.5" y2="19.5"/>
                      <line x1="4.5" y1="19.5" x2="6" y2="18"/>
                      <line x1="18" y1="6" x2="19.5" y2="4.5"/>
                    </g>
                  </g>
                  <circle cx="12" cy="12" r="6" fill="currentColor" opacity="0.2"/>
                  <path d="M9 12.5L11 14.5L15 9.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              );
              return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" strokeOpacity="0.4"/>
                  <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              );
            })()}
          </div>
          <div className="aivis-dash-kpi-body">
            <span className="aivis-dash-kpi-eyebrow">Status</span>
            <span className="aivis-dash-kpi-value aivis-dash-kpi-value--status">{dash?.last_run_status || '—'}</span>
            <span className="aivis-dash-kpi-label">Latest run</span>
            <span className="aivis-dash-kpi-hint">Most recent run lifecycle state</span>
            {crawl?.total > 0 && crawl.pending > 0 && (
              <div className="aivis-crawl-progress" aria-live="polite">
                <div className="aivis-crawl-progress-header">
                  <span className="aivis-crawl-progress-spinner" aria-hidden />
                  <span className="aivis-crawl-progress-label">
                    Checking for {selected?.client_name || 'client'}
                  </span>
                  <span className="aivis-crawl-progress-count">
                    {crawl.crawled}/{crawl.total}
                  </span>
                </div>
                <div className="aivis-crawl-progress-track" role="progressbar" aria-valuenow={crawl.crawled} aria-valuemin={0} aria-valuemax={crawl.total}>
                  <div
                    className="aivis-crawl-progress-fill"
                    style={{ width: `${Math.round((crawl.crawled / crawl.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
